// Construction de la carte en style low-poly : sols, murs et toits en couleurs unies, toits en pente, mobilier et décor
// fabriqués en formes simples (lowpoly.ts), coupe des murs autour du joueur et brouillard de guerre.
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { Building, FloorMat, GameMap, Rect } from '../shared/map';
import { PROP_MODELS } from '../shared/props';
import { buildLowPoly } from './lowpoly';

export interface World {
  root: THREE.Group;
  cut: { value: THREE.Vector3 }; // position du joueur pour la coupe des murs
  updateFog(px: number, py: number): void;
  updateRoofs(px: number, py: number): void;
  setDoors(open: Set<string>): void; // cache les portes fermées à clé déjà ouvertes
}

// ---------------------------------------------------------------- Modèles

interface Part { geo: THREE.BufferGeometry; mat: THREE.Material; local: THREE.Matrix4; tint?: number[] } // tint : couleurs possibles selon l'exemplaire
const models = new Map<string, Part[]>();

// Charge les modèles du décor et les ramène à une origine commune : centrés, posés au sol, à l'échelle du jeu
export async function loadProps(map: GameMap, onProgress?: (f: number) => void) {
  const loader = new GLTFLoader();
  const files = new Map<string, Promise<THREE.Group>>(); // un fichier peut servir à plusieurs variantes
  const fences = map.rects.some(r => r.look === 'chainlink') ? ['fence_panel', 'fence_post'] : [];
  const used = [...new Set([...map.props.map(p => p.model), ...fences])];
  let done = 0;
  await Promise.all(used.map(async name => {
    const def = PROP_MODELS[name];
    if (!def) return;
    // Modèle low-poly fait maison : rien à télécharger
    const low = buildLowPoly(name);
    if (low) {
      models.set(name, low.map(p => ({ geo: p.mesh.geometry, mat: p.mesh.material as THREE.Material, local: p.mesh.matrix.clone(), tint: p.tint })));
      onProgress?.(++done / used.length);
      return;
    }
    try {
      if (!files.has(def.file)) files.set(def.file, loader.loadAsync(`assets/models/${def.file}`).then(g => g.scene));
      const src = await files.get(def.file)!;
      src.updateMatrixWorld(true);
      // Variante choisie : un objet est gardé si lui ou l'un de ses parents porte un nom retenu
      const keep = def.keep ? new RegExp(def.keep) : null;
      const kept = (o: THREE.Object3D) => { for (let p: THREE.Object3D | null = o; p && p !== src; p = p.parent) if (keep!.test(p.name)) return true; return false; };
      const meshes: THREE.Mesh[] = [];
      src.traverse(o => { if ((o as THREE.Mesh).isMesh && (!keep || kept(o))) meshes.push(o as THREE.Mesh); });
      const box = new THREE.Box3();
      for (const m of meshes) box.expandByObject(m);
      const c = box.getCenter(new THREE.Vector3());
      const norm = new THREE.Matrix4().makeScale(def.scale, def.scale, def.scale).multiply(new THREE.Matrix4().makeTranslation(-c.x, def.rooted ? 0 : -box.min.y, -c.z));
      const parts: Part[] = meshes.map(m => ({ geo: m.geometry, mat: (Array.isArray(m.material) ? m.material[0] : m.material) as THREE.Material, local: norm.clone().multiply(m.matrixWorld) }));
      // Les arbres du pack Kenney sont saturés : feuillage vert olive éteint, troncs brun gris
      if (def.file.startsWith('nature/')) for (const p of parts) {
        const m = p.mat as THREE.MeshStandardMaterial;
        if (!m.color) continue;
        p.mat = m.clone();
        (p.mat as THREE.MeshStandardMaterial).color.set(m.color.g > m.color.r ? 0x4f5a33 : 0x4a3e33);
      }
      // Feuillage Poly Haven : découpe nette (alphaTest) plutôt que transparence, pour le tri et les ombres
      for (const p of parts) if (/twig|leaves|branch/i.test(p.mat.name) && (p.mat as THREE.MeshStandardMaterial).map) {
        p.mat = p.mat.clone();
        Object.assign(p.mat, { transparent: false, alphaTest: 0.45, depthWrite: true, side: THREE.DoubleSide });
      }
      // Grillage : le fil réduit en 512 px devient un voile gris, on l'allège
      for (const p of parts) if (/wire/i.test(p.mat.name)) { p.mat = p.mat.clone(); Object.assign(p.mat, { opacity: 0.45, depthWrite: false }); }
      models.set(name, parts);
    } catch { /* modèle absent : rien n'est dessiné */ }
    onProgress?.(++done / used.length);
  }));
}

// ---------------------------------------------------------------- Matériaux

const texLoader = new THREE.TextureLoader();
const texCache = new Map<string, THREE.Texture>();
function tex(file: string, color = true): THREE.Texture {
  if (texCache.has(file)) return texCache.get(file)!;
  const t = texLoader.load(`assets/tex/${file}`);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.colorSpace = color ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  t.anisotropy = 8;
  texCache.set(file, t);
  return t;
}

// Matériau réaliste Poly Haven : couleur, relief (normales) et rugosité, répétés tous les `size` mètres.
// rgb : correction de teinte (linéaire, peut dépasser 1) pour ramener une texture trop chaude ou trop sombre vers la palette
interface Surface { id: string; tint: number; size: number; macro?: boolean; rgb?: [number, number, number]; col: number } // col : couleur unie (low-poly)
// Style low-poly : couleur unie (col), facettes visibles ; les textures réalistes restent disponibles (LOW_POLY = false)
const LOW_POLY = true;
function pbr(s: Surface, tint = s.tint) {
  if (LOW_POLY) return new THREE.MeshStandardMaterial({ color: tint === s.tint ? s.col : tint, roughness: 0.95, flatShading: true, side: THREE.DoubleSide });
  const m = new THREE.MeshStandardMaterial({
    map: tex(`ph/${s.id}_diff.webp`), normalMap: tex(`ph/${s.id}_nor.webp`, false), roughnessMap: tex(`ph/${s.id}_rough.webp`, false),
    color: tint, roughness: 1, side: THREE.DoubleSide,
  });
  if (s.rgb) m.color.multiply(new THREE.Color().setRGB(...s.rgb, THREE.LinearSRGBColorSpace));
  return m;
}

// Sols : surfaces répétées dans le repère du monde
const FLOORS: Record<FloorMat | 'grass', Surface> = {
  grass: { id: 'sparse_grass', tint: 0xffffff, size: 5, macro: true, rgb: [1.35, 2.5, 4], col: 0x56612f },
  asphalt: { id: 'asphalt_02', tint: 0xb4b4b4, size: 6, col: 0x3f3f40 },
  parking: { id: 'asphalt_02', tint: 0xc8c6c2, size: 6, col: 0x4a4a4b },
  sidewalk: { id: 'concrete_tiles', tint: 0xffffff, size: 3, rgb: [1.45, 1.9, 2.7], col: 0x7f7b73 },
  concrete: { id: 'concrete_floor', tint: 0xffffff, size: 4, rgb: [0.55, 0.83, 1.45], col: 0x6f6b64 },
  gravel: { id: 'bicolour_gravel', tint: 0xb0aaa0, size: 3, col: 0x625b50, macro: true },
  dirt: { id: 'bicolour_gravel', tint: 0xa38e72, size: 4, macro: true, col: 0x6b5940 },
  wood: { id: 'brown_planks_04', tint: 0xd8cbbb, size: 3, col: 0x6e5540 },
  tiles: { id: 'floor_tiles_06', tint: 0xe6e3dc, size: 2.5, col: 0x9d998f },
};

// Murs et toits selon le style du bâtiment
const WALLS: Record<NonNullable<Building['style']>, Surface> = {
  plaster: { id: 'clay_plaster', tint: 0xffffff, size: 3, col: 0xa89a82 },
  render: { id: 'grey_plaster_02', tint: 0xffffff, size: 3, col: 0x8d8a83 },
  brick: { id: 'brick_wall_001', tint: 0xf2e6dc, size: 2.5, col: 0x8a5a46 },
  painted: { id: 'painted_brick', tint: 0xffffff, size: 2.5, col: 0x8f948f },
  metal: { id: 'corrugated_iron', tint: 0xd0d4d6, size: 2.5, col: 0x737c7e },
  wood: { id: 'brown_planks_04', tint: 0xc8b8a4, size: 2.5, col: 0x6e5440 },
};
const INNER: Surface = { id: 'grey_plaster', tint: 0xf1ede6, size: 3, col: 0xb3ada2 };
const CONCRETE: Surface = { id: 'concrete_floor', tint: 0xffffff, size: 3, rgb: [0.4, 0.6, 1.05], col: 0x5c5953 };
const ROOFS: Record<NonNullable<Building['roofKind']>, Surface> = {
  clay: { id: 'clay_roof_tiles', tint: 0xe8dcd4, size: 3, col: 0x8a4a36 },
  slate: { id: 'grey_roof_tiles', tint: 0xd8dade, size: 3, col: 0x4b5056 },
  metal: { id: 'corrugated_iron', tint: 0xb8bcbe, size: 2.5, col: 0x687174 },
  flat: { id: 'concrete_floor', tint: 0xffffff, size: 4, rgb: [0.48, 0.72, 1.26], col: 0x5f5c56 },
};
const roofOf = (b: Building) => b.roofKind ?? (((b.roof >> 16) & 255) > (b.roof & 255) + 24 ? 'clay' : 'slate');
// Teinte de façade : la couleur du bâtiment, éclaircie (elle multiplie une texture déjà colorée)
const wallTint = (b: Building) => LOW_POLY
  ? (b.style === 'brick' || b.style === 'metal' || b.style === 'wood' ? WALLS[b.style].col : new THREE.Color(b.wall).multiplyScalar(0.78).getHex())
  : b.style === 'brick' || b.style === 'metal' || b.style === 'wood' ? WALLS[b.style].tint : new THREE.Color(b.wall).lerp(new THREE.Color(0xffffff), 0.45).getHex();

// Variation à grande échelle de la teinte des sols (évite l'effet de carrelage des textures répétées)
function withMacro(m: THREE.MeshStandardMaterial) {
  m.onBeforeCompile = shader => {
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec2 vMacro;')
      .replace('#include <worldpos_vertex>', '#include <worldpos_vertex>\nvMacro = (modelMatrix * vec4(transformed, 1.0)).xz;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>
varying vec2 vMacro;
float mHash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float mNoise(vec2 p) { vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(mHash(i), mHash(i + vec2(1, 0)), f.x), mix(mHash(i + vec2(0, 1)), mHash(i + vec2(1, 1)), f.x), f.y); }`)
      .replace('#include <map_fragment>', `#include <map_fragment>
float mv = mNoise(vMacro * 0.045) * 0.6 + mNoise(vMacro * 0.13) * 0.4;
diffuseColor.rgb *= mix(vec3(0.74, 0.7, 0.6), vec3(1.08, 1.06, 0.98), mv);
float hv = mNoise(vMacro * 0.07 + 17.0);
diffuseColor.rgb *= mix(vec3(1.1, 1.04, 0.78), vec3(0.88, 1.05, 0.92), hv);`);
  };
  m.customProgramCacheKey = () => 'macro';
  return m;
}

// Matériau qui disparaît au-dessus de 1,3 m dans un cercle autour du joueur : on voit l'intérieur des pièces
function withCut<T extends THREE.Material>(m: T, cut: { value: THREE.Vector3 }): T {
  m.onBeforeCompile = shader => {
    shader.uniforms.uCut = cut;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vWorld;')
      .replace('#include <worldpos_vertex>', '#include <worldpos_vertex>\nvWorld = (modelMatrix * vec4(transformed, 1.0)).xyz;\n#ifdef USE_INSTANCING\nvWorld = (modelMatrix * instanceMatrix * vec4(transformed, 1.0)).xyz;\n#endif');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nuniform vec3 uCut;\nvarying vec3 vWorld;')
      .replace('void main() {', 'void main() {\n  if (vWorld.y > 1.3 && distance(vWorld.xz, uCut.xz) < 7.5) discard;');
  };
  m.customProgramCacheKey = () => 'cut';
  return m;
}

// Pavé de dimensions réelles dont les coordonnées de texture suivent les mètres (pas d'étirement sur les longs murs)
function texturedBox(cx: number, cy: number, cz: number, w: number, h: number, d: number, size: number) {
  const g = new THREE.BoxGeometry(w, h, d);
  const uv = g.attributes.uv!;
  const dims = [[d, h], [d, h], [w, d], [w, d], [w, h], [w, h]] as const;
  const off = ((cx + cz) % 7) / size;
  for (let f = 0; f < 6; f++) for (let k = 0; k < 4; k++) {
    const i = f * 4 + k;
    uv.setXY(i, uv.getX(i) * dims[f]![0] / size + off, uv.getY(i) * dims[f]![1] / size);
  }
  g.translate(cx, cy, cz);
  return g;
}

// Toit à deux pans (faîtage dans la longueur) : deux versants et deux pignons ; dalle pour les toits-terrasses
function roofGeometry(b: Building, kind: string, size: number): { roof: THREE.BufferGeometry; gables: THREE.BufferGeometry | null } {
  const o = 0.45, x0 = b.x0 - o, x1 = b.x1 + o, z0 = b.y0 - o, z1 = b.y1 + o, base = 3.0;
  if (kind === 'flat') return { roof: texturedBox((x0 + x1) / 2, base + 0.15, (z0 + z1) / 2, x1 - x0, 0.3, z1 - z0, size).toNonIndexed(), gables: null };
  const alongX = x1 - x0 >= z1 - z0;
  const span = alongX ? z1 - z0 : x1 - x0;
  const rise = Math.min(kind === 'metal' ? 1.2 : 2.4, span * (kind === 'metal' ? 0.15 : 0.38));
  const slope = Math.hypot(span / 2, rise) / size;
  const pos: number[] = [], uvs: number[] = [], gpos: number[] = [], guvs: number[] = [];
  const quad = (a: number[], b2: number[], c: number[], d: number[], L: number) => {
    pos.push(...a, ...b2, ...c, ...a, ...c, ...d);
    uvs.push(0, 0, L, 0, L, slope, 0, 0, L, slope, 0, slope);
  };
  // Pignon : triangle de maçonnerie sous le faîtage, au droit du mur
  const gable = (ax: number, az: number, bx: number, bz: number, tx: number, tz: number, len: number, h: number) => {
    gpos.push(ax, base, az, bx, base, bz, tx, base + h, tz);
    guvs.push(0, 0, len / 3, 0, len / 6, h / 3);
  };
  if (alongX) {
    const zm = (z0 + z1) / 2, L = (x1 - x0) / size, h = rise * ((b.y1 - b.y0) / span);
    quad([x0, base, z1], [x1, base, z1], [x1, base + rise, zm], [x0, base + rise, zm], L);
    quad([x1, base, z0], [x0, base, z0], [x0, base + rise, zm], [x1, base + rise, zm], L);
    gable(b.x0, b.y1, b.x0, b.y0, b.x0, zm, b.y1 - b.y0, h);
    gable(b.x1, b.y0, b.x1, b.y1, b.x1, zm, b.y1 - b.y0, h);
  } else {
    const xm = (x0 + x1) / 2, L = (z1 - z0) / size, h = rise * ((b.x1 - b.x0) / span);
    quad([x0, base, z0], [x0, base, z1], [xm, base + rise, z1], [xm, base + rise, z0], L);
    quad([x1, base, z1], [x1, base, z0], [xm, base + rise, z0], [xm, base + rise, z1], L);
    gable(b.x0, b.y0, b.x1, b.y0, xm, b.y0, b.x1 - b.x0, h);
    gable(b.x1, b.y1, b.x0, b.y1, xm, b.y1, b.x1 - b.x0, h);
  }
  const geo = (p: number[], u: number[]) => {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(p, 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute(u, 2));
    g.computeVertexNormals();
    return g;
  };
  return { roof: geo(pos, uvs), gables: geo(gpos, guvs) };
}

const box = new THREE.BoxGeometry(1, 1, 1);
const m4 = (x: number, y: number, z: number, sx: number, sy: number, sz: number, yaw = 0) =>
  new THREE.Matrix4().compose(new THREE.Vector3(x, y, z), new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), yaw), new THREE.Vector3(sx, sy, sz));

function instanced(geo: THREE.BufferGeometry, mat: THREE.Material, mats: THREE.Matrix4[], shadow = true) {
  const inst = new THREE.InstancedMesh(geo, mat, mats.length);
  mats.forEach((m, i) => inst.setMatrixAt(i, m));
  inst.castShadow = shadow;
  inst.receiveShadow = true;
  return inst;
}

// ---------------------------------------------------------------- Construction

export function buildWorld(map: GameMap, scene: THREE.Scene): World {
  const root = new THREE.Group();
  const cut = { value: new THREE.Vector3(-999, 0, -999) };

  // Sols : herbe partout, puis voirie et intérieurs (UV dans le repère du monde, pas de raccord visible)
  const floorMats = new Map<string, THREE.Material>();
  const matFor = (mat: FloorMat | 'grass') => {
    if (!floorMats.has(mat)) { const f = FLOORS[mat]; const m = pbr(f); m.side = THREE.FrontSide; floorMats.set(mat, f.macro ? withMacro(m) : m); }
    return floorMats.get(mat)!;
  };
  const floorMesh = (x0: number, y0: number, x1: number, y1: number, mat: FloorMat | 'grass', y: number) => {
    const f = FLOORS[mat];
    const w = x1 - x0, h = y1 - y0;
    const geo = new THREE.PlaneGeometry(w, h);
    const uv = geo.attributes.uv!;
    for (let i = 0; i < uv.count; i++) uv.setXY(i, (x0 + uv.getX(i) * w) / f.size, (y0 + (1 - uv.getY(i)) * h) / f.size);
    const mesh = new THREE.Mesh(geo, matFor(mat));
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.set(x0 + w / 2, y, y0 + h / 2);
    mesh.receiveShadow = true;
    root.add(mesh);
  };
  floorMesh(-40, -40, map.width + 40, map.height + 40, 'grass', 0);
  map.floors.forEach((f, i) => floorMesh(f.x0, f.y0, f.x1, f.y1, f.mat, 0.01 + (i % 8) * 0.002 + (f.mat === 'wood' || f.mat === 'tiles' ? 0.02 : 0)));

  // Marquage au sol et voie ferrée
  const paint = new THREE.MeshStandardMaterial({ color: 0xd8d4c8, roughness: 0.8 });
  const steel = new THREE.MeshStandardMaterial({ color: 0x5b5752, metalness: 0.7, roughness: 0.4 });
  const sleeperMat = new THREE.MeshStandardMaterial({ color: 0x4a3a2c, roughness: 1 });
  const soil = new THREE.MeshStandardMaterial({ color: 0x3e3226, roughness: 1 });
  const paintM: THREE.Matrix4[] = [], railM: THREE.Matrix4[] = [], sleepM: THREE.Matrix4[] = [], furrowM: THREE.Matrix4[] = [];
  for (const l of map.lines) {
    const w = l.x1 - l.x0, h = l.y1 - l.y0;
    if (l.rail) railM.push(m4(l.x0 + w / 2, 0.1, l.y0 + h / 2, w, 0.12, h));
    else if (l.furrow) furrowM.push(m4(l.x0 + w / 2, 0.03, l.y0 + h / 2, w, 0.12, h)); // sillons des champs
    else paintM.push(m4(l.x0 + w / 2, 0.035, l.y0 + h / 2, w, 0.01, h));
  }
  for (let x = 0.4; x < map.width; x += 0.9) for (const ty of [7.35, 10.35]) sleepM.push(m4(x, 0.04, ty, 0.25, 0.08, 2.4));
  root.add(instanced(box, paint, paintM, false), instanced(box, steel, railM, false), instanced(box, sleeperMat, sleepM, false), instanced(box, soil, furrowM, false));

  // Murs, linteaux, pignons et couvert bas : géométries fusionnées par matériau, texturées au mètre
  const batches = new Map<string, { s: Surface; tint: number; cut: boolean; geos: THREE.BufferGeometry[] }>();
  const add = (s: Surface, tint: number, g: THREE.BufferGeometry, withCutting = true) => {
    const k = `${s.id}|${s.rgb}|${tint}|${withCutting}|${g.index ? 1 : 0}`;
    if (!batches.has(k)) batches.set(k, { s, tint, cut: withCutting, geos: [] });
    batches.get(k)!.geos.push(g);
  };
  const glass: THREE.Matrix4[] = [], lit: THREE.Matrix4[] = [], frames: THREE.Matrix4[] = [];
  const chain: Rect[] = [], lockedDoors: Rect[] = [];
  for (const r of map.rects) {
    const w = r.x1 - r.x0, d = r.y1 - r.y0, cx = (r.x0 + r.x1) / 2, cz = (r.y0 + r.y1) / 2;
    if (r.look === 'model') continue;
    if (r.door) { lockedDoors.push(r); continue; }
    if (r.look === 'chainlink' || r.look === 'fence') { chain.push(r); continue; }
    if (r.look === 'basewall' || r.look === 'tower') { add(CONCRETE, r.look === 'tower' ? 0x8a8c86 : CONCRETE.tint, texturedBox(cx, r.height / 2, cz, w, r.height, d, CONCRETE.size)); continue; }
    if (r.kind === 'low') {
      const s = r.look === 'counter' ? FLOORS.wood : r.look === 'shelf' ? WALLS.metal : CONCRETE;
      add(s, r.look === 'shelf' ? 0x9aa0a4 : s.tint, texturedBox(cx, r.height / 2, cz, w, r.height, d, s.size), false);
      continue;
    }
    const b = r.bid !== undefined ? map.buildings[r.bid] : undefined;
    if (!(r.ext && b)) { add(INNER, INNER.tint, texturedBox(cx, r.height / 2, cz, w, r.height, d, INNER.size)); continue; }
    const s = WALLS[b.style ?? 'plaster'];
    add(s, wallTint(b), texturedBox(cx, r.height / 2, cz, w, r.height, d, s.size));
    add(CONCRETE, CONCRETE.tint, texturedBox(cx, 0.2, cz, w + (w > d ? 0 : 0.06), 0.4, d + (w > d ? 0.06 : 0), CONCRETE.size));
    // Fenêtres régulières sur les murs extérieurs assez longs
    const len = Math.max(w, d), horiz = w > d;
    const n = Math.floor((len - 1.2) / 3.4);
    for (let i = 0; i < n; i++) {
      const t = (i + 0.5) / n - 0.5;
      const wx = horiz ? cx + t * (len - 1.6) : cx, wz = horiz ? cz : cz + t * (len - 1.6);
      // Une fenêtre sur quatre environ est éclairée de l'intérieur (lumière chaude)
      (Math.abs(Math.sin(wx * 12.9898 + wz * 78.233)) % 1 < 0.27 ? lit : glass).push(m4(wx, 1.75, wz, horiz ? 1.2 : w + 0.05, 1.1, horiz ? d + 0.05 : 1.2));
      frames.push(m4(wx, 1.15, wz, horiz ? 1.4 : w + 0.12, 0.08, horiz ? d + 0.12 : 1.4));
    }
  }
  for (const l of map.lintels) {
    const b = map.buildings[l.bid]!, s = WALLS[b.style ?? 'plaster'];
    add(s, wallTint(b), texturedBox((l.x0 + l.x1) / 2, 2.65, (l.y0 + l.y1) / 2, l.x1 - l.x0, 0.7, l.y1 - l.y0, s.size));
  }
  root.add(instanced(box, withCut(new THREE.MeshStandardMaterial({ color: 0x1d2730, roughness: 0.1, metalness: 0.5 }), cut), glass, false));
  root.add(instanced(box, withCut(new THREE.MeshStandardMaterial({ color: 0xffd08a, emissive: 0xffb45a, emissiveIntensity: 1.3, roughness: 0.6 }), cut), lit, false));
  root.add(instanced(box, new THREE.MeshStandardMaterial({ color: 0xd9d4c8, roughness: 0.8 }), frames));
  for (const r of map.rects) if (r.look === 'fountain') {
    const water = new THREE.Mesh(new THREE.BoxGeometry(r.x1 - r.x0 - 0.5, 0.05, r.y1 - r.y0 - 0.5), new THREE.MeshStandardMaterial({ color: 0x2f4550, roughness: 0.05, metalness: 0.2 }));
    water.position.set((r.x0 + r.x1) / 2, r.height + 0.01, (r.y0 + r.y1) / 2);
    root.add(water);
  }

  // Portes fermées à clé : battant en acier, bandes jaunes et noires, voyant rouge ; cachées une fois ouvertes
  const doorMeshes = new Map<string, THREE.Object3D[]>();
  const steelDoor = new THREE.MeshStandardMaterial({ color: 0x4d5459, roughness: 0.5, metalness: 0.6 });
  const stripe = new THREE.MeshStandardMaterial({ color: 0xd9a52a, roughness: 0.6 }), stripeDark = new THREE.MeshStandardMaterial({ color: 0x1d1f21, roughness: 0.7 });
  const ledRed = new THREE.MeshStandardMaterial({ color: 0xff3020, emissive: 0xff2010, emissiveIntensity: 2 });
  for (const r of lockedDoors) {
    const w = r.x1 - r.x0, d = r.y1 - r.y0, horiz = w > d, len = Math.max(w, d);
    const g = new THREE.Group();
    g.position.set((r.x0 + r.x1) / 2, 0, (r.y0 + r.y1) / 2);
    if (!horiz) g.rotation.y = Math.PI / 2;
    const leaf = new THREE.Mesh(new THREE.BoxGeometry(len, r.height, 0.12), steelDoor);
    leaf.position.y = r.height / 2; leaf.castShadow = true; leaf.receiveShadow = true;
    g.add(leaf);
    for (let i = 0; i < 6; i++) {
      const s = new THREE.Mesh(new THREE.BoxGeometry(len / 6, 0.18, 0.14), i % 2 ? stripeDark : stripe);
      s.position.set(-len / 2 + len / 12 + (i * len) / 6, 0.35, 0);
      g.add(s);
    }
    for (const z of [-0.08, 0.08]) { const led = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.08, 0.03), ledRed); led.position.set(len / 2 - 0.25, 1.25, z); g.add(led); }
    root.add(g);
    if (!doorMeshes.has(r.door!)) doorMeshes.set(r.door!, []);
    doorMeshes.get(r.door!)!.push(g);
  }
  const setDoors = (open: Set<string>) => { for (const [id, ms] of doorMeshes) for (const m of ms) m.visible = !open.has(id); };

  // Toits en pente, retirés (avec leurs pignons) quand on est dedans
  const roofs: { meshes: THREE.Mesh[]; x0: number; y0: number; x1: number; y1: number }[] = [];
  const roofMats = new Map<string, THREE.Material>();
  for (const b of map.buildings) {
    const kind = roofOf(b), s = ROOFS[kind];
    // Double face : vu de l'intérieur (caméra à la 3e personne), le toit sert de plafond
    if (!roofMats.has(kind)) { const m = withCut(pbr(s), cut); m.side = THREE.DoubleSide; roofMats.set(kind, m); }
    const { roof, gables } = roofGeometry(b, kind, s.size);
    const meshes = [new THREE.Mesh(roof, roofMats.get(kind)!)];
    if (gables) { const ws = WALLS[b.style ?? 'plaster']; { const gm = withCut(pbr(ws, wallTint(b)), cut); gm.side = THREE.DoubleSide; meshes.push(new THREE.Mesh(gables, gm)); } }
    for (const m of meshes) { m.castShadow = m.receiveShadow = true; root.add(m); }
    roofs.push({ meshes, x0: b.x0, y0: b.y0, x1: b.x1, y1: b.y1 });
  }
  for (const { s, tint, cut: c, geos } of batches.values()) {
    const mesh = new THREE.Mesh(mergeGeometries(geos), c ? withCut(pbr(s, tint), cut) : pbr(s, tint));
    mesh.castShadow = mesh.receiveShadow = true;
    root.add(mesh);
  }

  // Objets du décor : une instance par pièce de modèle
  const placements = new Map<string, THREE.Matrix4[]>();
  const place = (model: string, m: THREE.Matrix4) => { if (!placements.has(model)) placements.set(model, []); placements.get(model)!.push(m); };
  for (const p of map.props) place(p.model, m4(p.x, 0, p.y, 1, 1, 1, p.free ?? (p.rot * Math.PI) / 2));
  // Grillages : panneaux de 1,91 m étirés pour tomber juste, un poteau à chaque jonction
  const PANEL = PROP_MODELS.fence_panel!.size[0];
  for (const r of chain) {
    const horiz = r.x1 - r.x0 >= r.y1 - r.y0, len = horiz ? r.x1 - r.x0 : r.y1 - r.y0;
    const n = Math.max(1, Math.round(len / PANEL)), seg = len / n;
    for (let i = 0; i <= n; i++) {
      const t = (horiz ? r.x0 : r.y0) + i * seg;
      const [x, z] = horiz ? [t, (r.y0 + r.y1) / 2] : [(r.x0 + r.x1) / 2, t];
      place('fence_post', m4(x, 0, z, 1, 1, 1));
      if (i < n) place('fence_panel', m4(horiz ? x + seg / 2 : x, 0, horiz ? z : z + seg / 2, seg / PANEL, 1, 1, horiz ? 0 : Math.PI / 2));
    }
  }
  for (const [name, mats] of placements) {
    const parts = models.get(name);
    if (!parts) continue;
    // (vue à la 3e personne : plus de découpe des objets hauts, le feuillage garde son matériau à vent)
    for (const part of parts) {
      const mat = part.mat;
      const inst = instanced(part.geo, mat, mats.map(m => m.clone().multiply(part.local)), !name.startsWith('fence_'));
      if (part.tint) mats.forEach((m, i) => { const h = Math.abs(Math.floor(m.elements[12]! * 7.31 + m.elements[14]! * 3.17)); inst.setColorAt(i, new THREE.Color(part.tint![h % part.tint!.length]!)); });
      root.add(inst);
    }
  }

  // Zones d'extraction : cercle vert au sol
  const extMat = new THREE.MeshBasicMaterial({ color: 0x7bbf6a, transparent: true, opacity: 0.6, depthWrite: false });
  const extFill = new THREE.MeshBasicMaterial({ color: 0x7bbf6a, transparent: true, opacity: 0.14, depthWrite: false });
  for (const e of map.extracts) {
    const ring = new THREE.Mesh(new THREE.RingGeometry(e.r - 0.15, e.r, 48), extMat);
    const disc = new THREE.Mesh(new THREE.CircleGeometry(e.r - 0.15, 48), extFill);
    for (const m of [ring, disc]) { m.rotation.x = -Math.PI / 2; m.position.set(e.x, 0.06, e.y); root.add(m); }
  }

  // Brouillard de guerre : ce que les murs cachent au joueur est assombri (ombres projetées depuis sa position)
  const walls = map.rects.filter((r: Rect) => r.kind === 'wall' && r.look !== 'fence');
  const fogGeo = new THREE.BufferGeometry();
  const fogPos = new Float32Array(walls.length * 4 * 6 * 3);
  fogGeo.setAttribute('position', new THREE.BufferAttribute(fogPos, 3).setUsage(THREE.DynamicDrawUsage));
  const fog = new THREE.Mesh(fogGeo, new THREE.MeshBasicMaterial({
    color: 0x05070a, transparent: true, opacity: 0.6, depthWrite: false, side: THREE.DoubleSide,
    stencilWrite: true, stencilRef: 1, stencilFunc: THREE.NotEqualStencilFunc, stencilZPass: THREE.ReplaceStencilOp,
  }));
  fog.frustumCulled = false;
  fog.renderOrder = 5;
  root.add(fog);
  const FAR = 160, Y = 0.07;

  function updateFog(px: number, py: number) {
    let n = 0;
    const quad = (ax: number, ay: number, bx: number, by: number) => {
      const da = Math.hypot(ax - px, ay - py) || 1e-3, db = Math.hypot(bx - px, by - py) || 1e-3;
      const fax = ax + ((ax - px) / da) * FAR, fay = ay + ((ay - py) / da) * FAR;
      const fbx = bx + ((bx - px) / db) * FAR, fby = by + ((by - py) / db) * FAR;
      for (const [x, y] of [[ax, ay], [bx, by], [fbx, fby], [ax, ay], [fbx, fby], [fax, fay]] as const) { fogPos[n++] = x; fogPos[n++] = Y; fogPos[n++] = y; }
    };
    for (const r of walls) {
      if (Math.min(Math.abs(r.x0 - px), Math.abs(r.x1 - px)) > 60 || Math.min(Math.abs(r.y0 - py), Math.abs(r.y1 - py)) > 60) continue;
      // Seuls les bords tournés vers le joueur projettent une ombre
      if (py < r.y0) quad(r.x0, r.y0, r.x1, r.y0);
      if (py > r.y1) quad(r.x0, r.y1, r.x1, r.y1);
      if (px < r.x0) quad(r.x0, r.y0, r.x0, r.y1);
      if (px > r.x1) quad(r.x1, r.y0, r.x1, r.y1);
    }
    fogPos.fill(0, n);
    fogGeo.setDrawRange(0, n / 3);
    fogGeo.attributes.position!.needsUpdate = true;
  }

  function updateRoofs(px: number, py: number) {
    for (const r of roofs) {
      const inside = px > r.x0 - 0.3 && px < r.x1 + 0.3 && py > r.y0 - 0.3 && py < r.y1 + 0.3;
      for (const m of r.meshes) m.visible = !inside;
    }
  }

  scene.add(root);
  return { root, cut, updateFog, updateRoofs, setDoors };
}
